// app/features/catalog/routes/Catalog.filters.ts
//
// Pure client-side filter pipeline applied to the rental products returned
// by useInfiniteProducts. Kept separate from Catalog.tsx so it can be
// unit-tested in isolation and the page count in the catalogue header
// matches the number of cards rendered.

import type { CustomerEquipmentTypeResponse, CustomerLocationDto } from "@/types";
import { hasMonthlyRentalPricing } from "@/components/pricing";
import { hasTieredPricing } from "@/lib/pricing";
import type { RentalMode } from "@/stores/useLocationStore";

export interface CatalogueFilterContext {
    rentalMode: RentalMode;
    location: CustomerLocationDto | null;
}

/**
 * Whether the catalogue should drop a product because ProductCard would
 * render it with the "Unavailable" badge. Mirrors ProductCard's own
 * `isUnavailable` derivation so the badge never appears in the grid.
 */
function isGloballyUnavailable(product: CustomerEquipmentTypeResponse): boolean {
    if (!product.isAvailableForRental) return true;
    const hasPricing = hasTieredPricing(product) || hasMonthlyRentalPricing(product);
    return !hasPricing;
}

export function applyCatalogueClientFilters(
    products: readonly CustomerEquipmentTypeResponse[],
    ctx: CatalogueFilterContext,
): CustomerEquipmentTypeResponse[] {
    let out = products.filter((p) => p.isVisibleToCustomers !== false);
    out = out.filter((p) => !isGloballyUnavailable(p));

    if (ctx.rentalMode === "subscription") {
        out = out.filter((p) => hasMonthlyRentalPricing(p));
    } else if (ctx.rentalMode === "short-term") {
        out = out.filter((p) => hasTieredPricing(p));
    }

    if (ctx.location) {
        out = out.filter((p) => p.availableQuantity > 0);
    }

    return out;
}
